// packages/activity/src/lib/scene-edits.ts
import type { AspectRecord, IdClock, WriteMap } from "./character-edits.js";

export interface ZoneRecord {
  id: string;
  name: string;
  aspects?: AspectRecord[];
}

export interface CountdownRecord {
  nameKey: string;
  max: number;
  current: number;
  triggerTextKey?: string;
  public?: boolean;
  visibility?: "revealed" | "hidden";
}

export interface EditableScene {
  status: "draft" | "active" | "ended" | "archived";
  createdAt: number;
  activatedAt?: number;
  endedAt?: number;
  archivedAt?: number;
  name: string;
  descKey?: string;
  zones?: ZoneRecord[];
  sceneAspects?: AspectRecord[];
  situationalAspects?: AspectRecord[];
  countdowns?: Record<string, CountdownRecord>;
  hazards?: unknown[];
}

function genId(kind: string, clock?: IdClock): string {
  if (clock?.idSeed) return clock.idSeed;
  return `${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function now(clock?: IdClock): number {
  return clock?.now ?? Date.now();
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

interface AspectInput {
  text: string;
  visibility?: "revealed" | "hidden";
}

export function addSceneAspect(
  path: string,
  scene: EditableScene,
  input: AspectInput,
  clock?: IdClock,
): WriteMap {
  const a: AspectRecord = {
    id: genId("sa", clock),
    text: input.text,
    kind: "scene",
    visibility: input.visibility ?? "revealed",
    lifetime: "scene",
    scope: "scene",
    createdAt: now(clock),
  };
  return { [`${path}/sceneAspects`]: [...(scene.sceneAspects ?? []), a] };
}

export function removeSceneAspect(path: string, scene: EditableScene, id: string): WriteMap {
  return {
    [`${path}/sceneAspects`]: (scene.sceneAspects ?? []).filter((a) => a.id !== id),
  };
}

export function addSituationalAspect(
  path: string,
  scene: EditableScene,
  input: AspectInput,
  clock?: IdClock,
): WriteMap {
  const a: AspectRecord = {
    id: genId("si", clock),
    text: input.text,
    kind: "situation",
    visibility: input.visibility ?? "revealed",
    lifetime: "exchange",
    scope: "scene",
    createdAt: now(clock),
  };
  return { [`${path}/situationalAspects`]: [...(scene.situationalAspects ?? []), a] };
}

export function removeSituationalAspect(path: string, scene: EditableScene, id: string): WriteMap {
  return {
    [`${path}/situationalAspects`]: (scene.situationalAspects ?? []).filter((a) => a.id !== id),
  };
}

export function addZone(path: string, scene: EditableScene, zone: ZoneRecord): WriteMap {
  return { [`${path}/zones`]: [...(scene.zones ?? []), zone] };
}

export function removeZone(path: string, scene: EditableScene, id: string): WriteMap {
  return { [`${path}/zones`]: (scene.zones ?? []).filter((z) => z.id !== id) };
}

export function renameZone(path: string, scene: EditableScene, id: string, name: string): WriteMap {
  return {
    [`${path}/zones`]: (scene.zones ?? []).map((z) => (z.id === id ? { ...z, name } : z)),
  };
}

export function setCountdown(path: string, id: string, record: CountdownRecord): WriteMap {
  return { [`${path}/countdowns/${id}`]: record };
}

export function adjustCountdown(
  path: string,
  scene: EditableScene,
  id: string,
  delta: number,
): WriteMap {
  const cd = scene.countdowns?.[id];
  if (!cd) return {};
  const next = clamp(cd.current + delta, 0, cd.max);
  return { [`${path}/countdowns/${id}/current`]: next };
}

export function removeCountdown(path: string, id: string): WriteMap {
  return { [`${path}/countdowns/${id}`]: null };
}

export function setSceneStatus(
  path: string,
  status: EditableScene["status"],
  clock?: IdClock,
): WriteMap {
  const stamp = now(clock);
  const writes: WriteMap = { [`${path}/status`]: status };
  if (status === "active") writes[`${path}/activatedAt`] = stamp;
  if (status === "ended") writes[`${path}/endedAt`] = stamp;
  if (status === "archived") writes[`${path}/archivedAt`] = stamp;
  return writes;
}
